// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminSecurity\AdminSecurityLogs.js
// Версия: 2.0.0 - Light theme, real data
// Дата: 04.12.2025

import React, { useState, useEffect } from 'react';
import './AdminSecurityLogs.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const ACTION_LABELS = {
  login_success: { label: 'Вход', icon: '🔓', color: 'green' },
  login_failed: { label: 'Ошибка входа', icon: '🔐', color: 'red' },
  admin_login: { label: 'Вход админа', icon: '👑', color: 'purple' },
  logout: { label: 'Выход', icon: '🚪', color: 'gray' },
  voucher_created: { label: 'Подарок создан', icon: '🎁', color: 'blue' },
  voucher_claimed: { label: 'Подарок получен', icon: '📥', color: 'green' },
  withdrawal_request: { label: 'Запрос вывода', icon: '💸', color: 'orange' },
  deposit_confirmed: { label: 'Депозит', icon: '💰', color: 'green' },
  user_blocked: { label: 'Блокировка', icon: '🚫', color: 'red' },
  settings_changed: { label: 'Настройки', icon: '⚙️', color: 'blue' },
  password_changed: { label: 'Смена пароля', icon: '🔑', color: 'orange' }
};

const AdminSecurityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const token = localStorage.getItem('authToken');

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/security/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setLogs(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: logs.length,
    logins: logs.filter(l => l.action?.includes('login')).length,
    errors: logs.filter(l => l.action?.includes('failed') || l.action?.includes('blocked')).length,
    today: logs.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length
  };

  const uniqueActions = [...new Set(logs.map(l => l.action))];

  const filteredLogs = logs.filter(l => {
    if (actionFilter !== 'all' && l.action !== actionFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const searchIn = [l.action, l.ip_address, l.user_email, l.user_agent, JSON.stringify(l.details)].join(' ').toLowerCase();
      if (!searchIn.includes(q)) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getAction = (action) => ACTION_LABELS[action] || { label: action, icon: '📋', color: 'gray' };

  const exportCSV = () => {
    const headers = ['Дата', 'Действие', 'Email', 'IP', 'User Agent', 'Детали'];
    const rows = filteredLogs.map(l => [
      formatDate(l.created_at),
      getAction(l.action).label,
      l.user_email || '-',
      l.ip_address || '-',
      l.user_agent || '-',
      JSON.stringify(l.details || {})
    ]);
    const bom = '\uFEFF';
    const csv = bom + [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) return <div className="admin-loading">Загрузка...</div>;

  return (
    <div className="admin-logs">
      <div className="admin-page-header">
        <h1 className="admin-page-title">📋 Логи безопасности</h1>
        <div className="admin-page-actions">
          <button className="admin-btn" onClick={exportCSV}>📥 CSV</button>
          <button className="admin-btn admin-btn-primary" onClick={fetchLogs}>🔄</button>
        </div>
      </div>

      {/* Stats */}
      <div className="log-stats">
        <div className="log-stat" onClick={() => setActionFilter('all')}>
          <span className="log-stat-value">{stats.total}</span>
          <span className="log-stat-label">Всего</span>
        </div>
        <div className="log-stat blue">
          <span className="log-stat-value">{stats.logins}</span>
          <span className="log-stat-label">Входы</span>
        </div>
        <div className="log-stat red">
          <span className="log-stat-value">{stats.errors}</span>
          <span className="log-stat-label">Ошибки</span>
        </div>
        <div className="log-stat green">
          <span className="log-stat-value">{stats.today}</span>
          <span className="log-stat-label">Сегодня</span>
        </div>
      </div>

      {/* Search */}
      <div className="log-search">
        <input
          type="text"
          placeholder="🔍 Поиск по IP, email, действию..."
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
      </div>

      {/* Filters */}
      <div className="log-filters">
        <label>Действие:</label>
        <div className="filter-buttons">
          <button className={actionFilter === 'all' ? 'active' : ''} onClick={() => { setActionFilter('all'); setCurrentPage(1); }}>
            📋 Все
          </button>
          {uniqueActions.map(action => {
            const info = getAction(action);
            return (
              <button 
                key={action} 
                className={actionFilter === action ? 'active' : ''}
                onClick={() => { setActionFilter(action); setCurrentPage(1); }}
              >
                {info.icon} {info.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="admin-card">
        {paginatedLogs.length === 0 ? (
          <div className="admin-empty">📭 Логи не найдены</div>
        ) : (
          <div className="log-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Действие</th>
                  <th>Пользователь</th>
                  <th>IP</th>
                  <th>User Agent</th>
                  <th>Детали</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map(log => {
                  const actionInfo = getAction(log.action);
                  return (
                    <tr key={log.id}>
                      <td className="log-date">{formatDate(log.created_at)}</td>
                      <td>
                        <span className={`log-action ${actionInfo.color}`}>
                          {actionInfo.icon} {actionInfo.label}
                        </span>
                      </td>
                      <td className="log-user">{log.user_email || '-'}</td>
                      <td><code className="log-ip">{log.ip_address || '-'}</code></td>
                      <td className="log-ua" title={log.user_agent}>{log.user_agent?.substring(0, 30) || '-'}</td>
                      <td className="log-details">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <code title={JSON.stringify(log.details)}>{JSON.stringify(log.details).substring(0, 40)}...</code>
                        ) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="log-pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
            <span>{currentPage} / {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        )}

        <div className="log-footer">Показано: {paginatedLogs.length} из {filteredLogs.length}</div>
      </div>
    </div>
  );
};

export default AdminSecurityLogs;